import type { WpScanResult } from "@/types/scan";
import {
  Shield, AlertTriangle, Info, CheckCircle, XCircle, ExternalLink,
  Plug, Palette, Users, FileWarning, Globe, Download, ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const severityConfig = {
  critical: { color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", icon: XCircle, label: "CRITICAL" },
  high: { color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/30", icon: AlertTriangle, label: "HIGH" },
  medium: { color: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30", icon: Info, label: "MEDIUM" },
  low: { color: "text-primary", bg: "bg-primary/10 border-primary/30", icon: Info, label: "LOW" },
  info: { color: "text-muted-foreground", bg: "bg-muted border-muted-foreground/20", icon: Info, label: "INFO" },
};

const generateTextReport = (result: WpScanResult): string => {
  const lines: string[] = [];
  lines.push("═══════════════════════════════════════════════════════");
  lines.push("  SHADOWSCAN SECURITY ASSESSMENT REPORT");
  lines.push("═══════════════════════════════════════════════════════");
  lines.push("");
  lines.push(`Target:       ${result.target}`);
  lines.push(`Scan Date:    ${new Date(result.scanDate).toLocaleString()}`);
  lines.push(`Site Type:    ${(result.siteType || "generic").toUpperCase()}`);
  if (result.server?.technology?.length) {
    lines.push(`Technologies: ${result.server.technology.filter((t) => !t.includes(":")).join(", ") || result.server.technology.join(", ")}`);
  }
  if (result.wordpress.detected) {
    lines.push(`WordPress:    v${result.wordpress.version || "unknown"}${result.wordpress.outdated ? ` (OUTDATED - Latest: v${result.wordpress.latestVersion})` : ""}`);
  }
  lines.push("");
  const crits = result.vulnerabilities.filter((v) => v.severity === "critical").length;
  const highs = result.vulnerabilities.filter((v) => v.severity === "high").length;
  const meds = result.vulnerabilities.filter((v) => v.severity === "medium").length;
  const lows = result.vulnerabilities.filter((v) => v.severity === "low").length;
  lines.push("─── SUMMARY ───────────────────────────────────────────");
  lines.push(`Total Vulnerabilities: ${result.vulnerabilities.length}`);
  lines.push(`  Critical: ${crits}  |  High: ${highs}  |  Medium: ${meds}  |  Low: ${lows}`);
  lines.push(`Exposed Files: ${result.exposedFiles.length}`);
  lines.push(`Plugins: ${result.plugins.length} (${result.plugins.filter((p) => p.outdated).length} outdated)`);
  lines.push(`Themes: ${result.themes.length} (${result.themes.filter((t) => t.outdated).length} outdated)`);
  lines.push(`Users Enumerated: ${result.users.length}`);
  lines.push("");
  if (result.vulnerabilities.length) {
    lines.push("─── VULNERABILITIES ───────────────────────────────────");
    result.vulnerabilities.forEach((v, i) => {
      lines.push(`\n[${i + 1}] ${v.title}`);
      lines.push(`    Severity: ${v.severity.toUpperCase()}${v.cvss ? ` | CVSS: ${v.cvss}` : ""}`);
      lines.push(`    ${v.description}`);
      if (v.remediation) lines.push(`    Remediation: ${v.remediation}`);
    });
    lines.push("");
  }
  if (result.securityHeaders.length) {
    lines.push("─── SECURITY HEADERS ──────────────────────────────────");
    result.securityHeaders.forEach((h) => {
      lines.push(`  ${h.present ? "✓" : "✗"} ${h.header}: ${h.present ? h.value : "MISSING"}`);
      if (!h.present && h.recommendation) lines.push(`    → ${h.recommendation}`);
    });
    lines.push("");
  }
  if (result.recommendations.length) {
    lines.push("─── RECOMMENDATIONS ───────────────────────────────────");
    result.recommendations.forEach((r) => {
      lines.push(`  [${r.priority.toUpperCase()}] ${r.title}`);
      lines.push(`    ${r.description}`);
    });
    lines.push("");
  }
  lines.push("═══════════════════════════════════════════════════════");
  lines.push("  Generated by ShadowScan // shadowscan.com.au");
  lines.push("═══════════════════════════════════════════════════════");
  return lines.join("\n");
};

export const WpScanResults = ({ result }: { result: WpScanResult }) => {
  const critCount = result.vulnerabilities.filter((v) => v.severity === "critical").length;
  const highCount = result.vulnerabilities.filter((v) => v.severity === "high").length;
  const medCount = result.vulnerabilities.filter((v) => v.severity === "medium").length;
  const lowCount = result.vulnerabilities.filter((v) => v.severity === "low").length;
  const outdatedPlugins = result.plugins.filter((p) => p.outdated);
  const outdatedThemes = result.themes.filter((t) => t.outdated);

  const handleDownloadReport = () => {
    const report = generateTextReport(result);
    const blob = new Blob([report], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `shadowscan-report-${result.target.replace(/[^a-z0-9]/gi, "-")}-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full space-y-6 mt-6">
      {/* Summary Banner */}
      <div className="border border-primary/30 rounded-lg p-6 bg-secondary/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-primary" />
            <h2 className="font-heading text-xl font-bold">Scan Report</h2>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={handleDownloadReport} className="gap-1.5 font-mono text-xs">
              <Download className="w-3.5 h-3.5" />Download Report
            </Button>
            <span className="text-xs font-mono text-muted-foreground">
              {new Date(result.scanDate).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="font-mono text-sm space-y-1 text-muted-foreground mb-4">
          <p>Target: <span className="text-foreground">{result.target}</span></p>
          <p>Site Type: <span className="text-primary font-bold uppercase">{result.siteType || "generic"}</span></p>
          {result.server?.technology && result.server.technology.length > 0 && (
            <p>Technologies: <span className="text-foreground">{result.server.technology.filter((t) => !t.includes(":")).join(", ") || result.server.technology.join(", ")}</span></p>
          )}
          {result.wordpress.detected && (
            <>
              <p>
                WordPress: <span className="text-primary">Detected (v{result.wordpress.version || "unknown"})</span>
                {result.wordpress.outdated && result.wordpress.latestVersion && (
                  <span className="text-red-400 ml-2 font-bold">⚠ OUTDATED - Latest: v{result.wordpress.latestVersion}</span>
                )}
              </p>
              <p>Plugins: <span className="text-foreground">{result.plugins.length}</span>
                {outdatedPlugins.length > 0 && <span className="text-red-400 ml-1">({outdatedPlugins.length} outdated)</span>}
              </p>
              <p>Themes: <span className="text-foreground">{result.themes.length}</span>
                {outdatedThemes.length > 0 && <span className="text-red-400 ml-1">({outdatedThemes.length} outdated)</span>}
              </p>
              <p>Users Found: <span className="text-foreground">{result.users.length}</span></p>
            </>
          )}
          <p>Exposed Files: <span className="text-foreground">{result.exposedFiles.length}</span></p>
          <p>Vulnerabilities: <span className={result.vulnerabilities.length > 0 ? "text-red-400 font-bold" : "text-foreground"}>{result.vulnerabilities.length}</span></p>
        </div>

        {/* Severity Summary Grid */}
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <div className="text-2xl font-heading font-bold text-red-400">{critCount}</div>
            <div className="text-xs font-mono text-red-400/70">CRITICAL</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
            <div className="text-2xl font-heading font-bold text-orange-400">{highCount}</div>
            <div className="text-xs font-mono text-orange-400/70">HIGH</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="text-2xl font-heading font-bold text-amber-300">{medCount}</div>
            <div className="text-xs font-mono text-amber-300/70">MEDIUM</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted border border-muted-foreground/20">
            <div className="text-2xl font-heading font-bold text-muted-foreground">{lowCount}</div>
            <div className="text-xs font-mono text-muted-foreground/70">LOW</div>
          </div>
        </div>
      </div>

      {/* Vulnerabilities */}
      {result.vulnerabilities.length > 0 && (
        <section>
          <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4">
            <ShieldAlert className="w-5 h-5 text-orange-400" />
            Vulnerabilities ({result.vulnerabilities.length})
          </h3>
          <div className="space-y-3">
            {result.vulnerabilities.map((vuln) => {
              const config = severityConfig[vuln.severity];
              const Icon = config.icon;
              return (
                <div key={vuln.id} className={`border rounded-lg p-4 ${config.bg}`}>
                  <div className="flex items-start gap-3">
                    <Icon className={`w-5 h-5 mt-0.5 ${config.color} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${config.color} bg-background/50`}>
                          {config.label}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground">{vuln.id}</span>
                        {vuln.cvss && (
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-background/50 text-foreground/70">
                            CVSS: {vuln.cvss}
                          </span>
                        )}
                      </div>
                      <h4 className="font-heading font-semibold mt-1">{vuln.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1 font-mono">{vuln.description}</p>
                      {vuln.remediation && (
                        <div className="mt-3 p-3 rounded bg-background/50 border border-primary/10">
                          <p className="text-xs font-mono text-primary font-semibold mb-1">⚡ REMEDIATION:</p>
                          <p className="text-xs font-mono text-foreground/80 whitespace-pre-line">{vuln.remediation}</p>
                        </div>
                      )}
                      {vuln.references.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {vuln.references.map((ref, i) => (
                            <a key={i} href={ref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline">
                              <ExternalLink className="w-3 h-3" />
                              {(() => { try { return new URL(ref).hostname; } catch { return ref; } })()}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Plugins */}
      {result.plugins.length > 0 && (
        <section>
          <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4">
            <Plug className="w-5 h-5 text-primary" />
            Detected Plugins ({result.plugins.length})
            {outdatedPlugins.length > 0 && (
              <span className="text-xs font-mono text-red-400 bg-red-500/10 px-2 py-0.5 rounded">
                {outdatedPlugins.length} OUTDATED
              </span>
            )}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {result.plugins.map((plugin) => (
              <div key={plugin.slug} className={`border rounded-lg p-4 bg-secondary/30 ${plugin.outdated ? "border-red-500/40 bg-red-500/5" : "border-border/50"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-mono text-sm font-semibold">{plugin.name}</h4>
                    <p className="text-xs text-muted-foreground font-mono mt-1">
                      v{plugin.version || "?"}
                      {plugin.outdated && plugin.latestVersion && (
                        <span className="text-red-400 ml-1">→ v{plugin.latestVersion}</span>
                      )}
                    </p>
                  </div>
                  {plugin.outdated && <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />}
                  {!plugin.outdated && <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />}
                </div>
                {plugin.wpOrgUrl && (
                  <a href={plugin.wpOrgUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline font-mono mt-2 inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> wp.org
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Themes */}
      {result.themes.length > 0 && (
        <section>
          <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4">
            <Palette className="w-5 h-5 text-primary" />
            Detected Themes ({result.themes.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {result.themes.map((theme) => (
              <div key={theme.slug} className={`border rounded-lg p-4 bg-secondary/30 ${theme.outdated ? "border-red-500/40 bg-red-500/5" : "border-border/50"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-mono text-sm font-semibold">{theme.name}</h4>
                    <p className="text-xs text-muted-foreground font-mono mt-1">
                      v{theme.version || "?"}
                      {theme.outdated && theme.latestVersion && (
                        <span className="text-red-400 ml-1">→ v{theme.latestVersion}</span>
                      )}
                    </p>
                  </div>
                  {theme.outdated && <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />}
                  {!theme.outdated && <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Users */}
      {result.users.length > 0 && (
        <section>
          <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            Enumerated Users ({result.users.length})
          </h3>
          <div className="space-y-2">
            {result.users.map((user) => (
              <div key={`${user.id}-${user.username}`} className="border border-border/50 rounded-lg p-3 bg-secondary/30 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-mono text-primary font-bold">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-mono text-sm font-semibold">{user.username}</p>
                  <p className="text-xs text-muted-foreground font-mono">ID: {user.id} | {user.method}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Exposed Files */}
      {result.exposedFiles.length > 0 && (
        <section>
          <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4">
            <FileWarning className="w-5 h-5 text-orange-400" />
            Exposed Files ({result.exposedFiles.length})
          </h3>
          <div className="space-y-2">
            {result.exposedFiles.map((file) => {
              const riskConfig = severityConfig[file.risk] || severityConfig.low;
              return (
                <div key={file.path} className={`border rounded-lg p-3 ${riskConfig.bg}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${riskConfig.color} bg-background/50`}>
                      {file.risk.toUpperCase()}
                    </span>
                    <code className="text-sm font-mono text-foreground">{file.path}</code>
                    <span className="text-xs text-muted-foreground ml-auto">HTTP {file.status}</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono mt-1">{file.description}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Security Headers */}
      {result.securityHeaders.length > 0 && (
        <section>
          <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-primary" />
            Security Headers
          </h3>
          <div className="space-y-2">
            {result.securityHeaders.map((header) => (
              <div key={header.header} className={`border rounded-lg p-3 ${header.present ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`}>
                <div className="flex items-center gap-2">
                  {header.present ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                  )}
                  <span className="font-mono text-sm font-semibold">{header.header}</span>
                  {header.present && header.value && (
                    <span className="text-xs font-mono text-muted-foreground truncate max-w-[300px]">{header.value}</span>
                  )}
                </div>
                {!header.present && header.recommendation && (
                  <p className="text-xs text-muted-foreground font-mono mt-1 ml-6">→ {header.recommendation}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <section>
          <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Recommendations
          </h3>
          <div className="space-y-3">
            {result.recommendations.map((rec, i) => {
              const prioConfig = severityConfig[rec.priority] || severityConfig.medium;
              return (
                <div key={i} className={`border rounded-lg p-4 ${prioConfig.bg}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${prioConfig.color} bg-background/50`}>
                      {rec.priority.toUpperCase()}
                    </span>
                    <span className="text-xs font-mono text-muted-foreground">{rec.category}</span>
                  </div>
                  <h4 className="font-heading font-semibold text-sm">{rec.title}</h4>
                  <p className="text-xs text-muted-foreground font-mono mt-1">{rec.description}</p>
                  {rec.link && (
                    <a href={rec.link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline font-mono mt-2 inline-flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Learn more
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* CTA */}
      <div className="border border-primary/30 rounded-lg p-6 bg-primary/5 text-center">
        <Shield className="w-8 h-8 text-primary mx-auto mb-3" />
        <h3 className="font-heading text-lg font-bold mb-2">Need help fixing these issues?</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Start with free ShadowScan Guard for essential local hardening, use Basic Hosting if you need hosted WordPress onboarding, or move to Essential or Premium when you need monitored security and analyst-led response support.
        </p>
        <a href="/services" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-mono text-sm hover:bg-primary/90 transition-colors">
          View plans and Guard options
        </a>
      </div>
    </div>
  );
};
